import React from "react";
import { calculateTotal, calculatePercentOfTarget } from "../utils/budgetUtils";
import { calculatePercentThruMonth } from "../utils/calendarUtils";

/**
 * Income received this month against the monthly goal.
 *
 * The bar tracks **received cash only**. Outstanding money is shown as a
 * separate muted line rather than folded into the total, because the whole
 * point of the cash-basis model is that you can't spend what you haven't been
 * paid.
 *
 * Colour semantics are inverted relative to BudgetSummary: for income, being
 * ahead of the month's pace is good.
 *
 * @param {Object} props
 * @param {number|null} props.goal Monthly income goal; null until the profile loads.
 * @param {Object[]} props.incomeThisMonth
 * @param {Object[]} [props.outstanding] Earned but unpaid entries.
 */
const IncomeGoalCard = ({ goal, incomeThisMonth, outstanding = [] }) => {
  // goal is null until the profile loads, and may legitimately be 0.
  const target = Number(goal);
  const hasGoal = Number.isFinite(target) && target > 0;

  const received = calculateTotal(incomeThisMonth);
  const outstandingTotal = calculateTotal(outstanding);

  // Both are numbers, so the comparison below is numeric. Formatting to a
  // string happens only at render — comparing formatted strings would sort
  // them lexicographically ("9" > "50").
  const percentMonth = calculatePercentThruMonth();
  const percentGoal = calculatePercentOfTarget(incomeThisMonth, goal);

  let barColor = "bg-emerald-400";
  if (!hasGoal) {
    barColor = "bg-gray-300";
  } else if (percentGoal < percentMonth) {
    barColor = "bg-amber-400";
  }

  return (
    <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-5">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">
            Income This Month
          </p>
          <h3 className="text-3xl font-bold text-gray-800 mt-1">
            ${received.toFixed(0)}
          </h3>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Goal</p>
          <p className="text-sm font-semibold text-gray-700">
            {hasGoal ? `$${target.toFixed(0)}` : "—"}
          </p>
        </div>
      </div>

      {hasGoal ? (
        <>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-400">Goal Progress</span>
            <span className="font-semibold text-gray-600">
              {percentGoal.toFixed(0)}%
            </span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${barColor}`}
              style={{ width: `${Math.min(percentGoal, 100)}%` }}
            ></div>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-400">No income goal set</p>
      )}

      {outstandingTotal > 0 && (
        <p className="mt-3 text-sm text-gray-400">
          <span className="font-semibold text-gray-500">
            +${outstandingTotal.toFixed(0)}
          </span>{" "}
          outstanding
        </p>
      )}
    </div>
  );
};

export default IncomeGoalCard;
